// src/capa/CoverReadyFlow.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Alert,
  LinearProgress,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import FlowActions from "components/FlowActions";
import { PAPER_TYPES, calculateSpineWidth, cmToMm } from "./pdfUtils";

const canvasToProductCoverJpeg = (sourceCanvas) => {
  const maxDimension = 2000;
  const maxBytes = 330 * 1024;
  const sourceWidth = Math.floor(sourceCanvas.width / 2);
  const sourceHeight = sourceCanvas.height;
  const ratio = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(sourceWidth * ratio);
  canvas.height = Math.round(sourceHeight * ratio);

  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    sourceCanvas,
    sourceCanvas.width - sourceWidth,
    0,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );

  for (const quality of [0.86, 0.78, 0.7, 0.62, 0.54]) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    const size = Math.ceil(((dataUrl.length - "data:image/jpeg;base64,".length) * 3) / 4);

    if (size <= maxBytes || quality === 0.54) {
      return dataUrl;
    }
  }

  return canvas.toDataURL("image/jpeg", 0.54);
};

/**
 * Fluxo da CAPA PRONTA:
 *  A) Checklist + medidas da capa
 *  B) Upload / ajuste de tamanho da capa
 *
 * Props esperadas:
 *  - pdfInfo: { pages, widthCm, heightCm } (do miolo)
 *  - onClose?: fecha o modal
 *  - onFinish?: chamado ao enviar a capa
 */
export default function CoverReadyFlow({ pdfInfo, onClose, onFinish }) {
  const [step, setStep] = useState("A");
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [step]);
  const [hasFlap, setHasFlap] = useState(false);
  const [paperId, setPaperId] = useState(PAPER_TYPES[0].id);
  const [isChecklistDialogOpen, setIsChecklistDialogOpen] = useState(true);

  // checklist
  const [checkedTutorial, setCheckedTutorial] = useState(false);
  const [checkedSizes, setCheckedSizes] = useState(false);
  const [checkedBarcode, setCheckedBarcode] = useState(false);

  const paper = PAPER_TYPES.find((p) => p.id === paperId) || PAPER_TYPES[0];

  const { spineMm, pageWidthMm, pageHeightMm, totalWidthMm, totalHeightMm, totalWidthMmWithFlap } =
    useMemo(() => {
      if (!pdfInfo) {
        return {
          spineMm: 0,
          pageWidthMm: 0,
          pageHeightMm: 0,
          totalWidthMm: 0,
          totalHeightMm: 0,
          totalWidthMmWithFlap: 0,
        };
      }

      const pages = pdfInfo.pages || 0;

      // miolo em mm
      const pageWidthMm = cmToMm(pdfInfo.widthCm);
      const pageHeightMm = cmToMm(pdfInfo.heightCm);

      const bleedMm = 5; // sangria (topo, base e laterais externas)
      const flapMm = 70; // largura padrão de orelha (pode mudar depois)

      const spineMm = calculateSpineWidth(pages, paper.thicknessMicron);

      // largura de cada “capa” = largura do miolo + 5 mm (sangria externa)
      const panelWidthMm = pageWidthMm + bleedMm;

      const totalWidthMm = 2 * panelWidthMm + spineMm; // sem orelha
      const totalWidthMmWithFlap = totalWidthMm + 2 * flapMm; // com orelha
      const totalHeightMm = pageHeightMm + 2 * bleedMm;

      return {
        spineMm,
        pageWidthMm,
        pageHeightMm,
        totalWidthMm,
        totalHeightMm,
        totalWidthMmWithFlap,
      };
    }, [pdfInfo, paper]);

  const canGoNext = checkedTutorial && checkedSizes && checkedBarcode && !!pdfInfo;

  if (step === "B") {
    return (
      <CoverUploadStep
        hasFlap={hasFlap}
        setHasFlap={setHasFlap}
        paper={paper}
        spineMm={spineMm}
        totalWidthMm={hasFlap ? totalWidthMmWithFlap : totalWidthMm}
        totalHeightMm={totalHeightMm}
        onBack={() => setStep("A")}
        onClose={onClose}
        onFinish={onFinish}
      />
    );
  }

  return (
    <CoverChecklistStep
      pdfInfo={pdfInfo}
      paperId={paperId}
      setPaperId={setPaperId}
      hasFlap={hasFlap}
      setHasFlap={setHasFlap}
      spineMm={spineMm}
      pageWidthMm={pageWidthMm}
      pageHeightMm={pageHeightMm}
      totalWidthMm={totalWidthMm}
      totalHeightMm={totalHeightMm}
      totalWidthMmWithFlap={totalWidthMmWithFlap}
      checkedTutorial={checkedTutorial}
      setCheckedTutorial={setCheckedTutorial}
      checkedSizes={checkedSizes}
      setCheckedSizes={setCheckedSizes}
      checkedBarcode={checkedBarcode}
      setCheckedBarcode={setCheckedBarcode}
      isChecklistDialogOpen={isChecklistDialogOpen}
      setIsChecklistDialogOpen={setIsChecklistDialogOpen}
      canGoNext={canGoNext}
      onNext={() => setStep("B")}
      onClose={onClose}
    />
  );
}

/**
 * Tela A — checklist + mock das medidas (print A)
 */
function CoverChecklistStep({
  pdfInfo,
  paperId,
  setPaperId,
  hasFlap,
  setHasFlap,
  spineMm,
  pageWidthMm,
  pageHeightMm,
  totalWidthMm,
  totalHeightMm,
  totalWidthMmWithFlap,
  checkedTutorial,
  setCheckedTutorial,
  checkedSizes,
  setCheckedSizes,
  checkedBarcode,
  setCheckedBarcode,
  isChecklistDialogOpen,
  setIsChecklistDialogOpen,
  canGoNext,
  onNext,
  onClose,
}) {
  const formatMm = (value) => (value ? `${value.toFixed(1).replace(".", ",")} mm` : "-");

  const totalWidthLabel = hasFlap ? formatMm(totalWidthMmWithFlap) : formatMm(totalWidthMm);
  const checklistItems = [
    {
      checked: checkedTutorial,
      onChange: setCheckedTutorial,
      text: "Já assisti ao vídeo de como fazer a capa para publicar na plataforma.",
    },
    {
      checked: checkedSizes,
      onChange: setCheckedSizes,
      text: "Minha capa segue exatamente as medidas informadas pela plataforma.",
    },
    {
      checked: checkedBarcode,
      onChange: setCheckedBarcode,
      text: "Deixei espaço na contracapa para o código de barras / ISBN.",
    },
  ];
  const measureItems = [
    { label: "Miolo", value: pdfInfo ? `${pdfInfo.pages} páginas` : "-" },
    {
      label: "Formato",
      value: pdfInfo ? `${pdfInfo.widthCm} x ${pdfInfo.heightCm} cm` : "-",
    },
    { label: "Lombada", value: formatMm(spineMm) },
    { label: "Largura", value: totalWidthLabel },
    { label: "Altura", value: formatMm(totalHeightMm) },
    { label: "Orelha", value: hasFlap ? "Com orelha" : "Sem orelha" },
  ];
  const checkedCount = checklistItems.filter((item) => item.checked).length;

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Dialog
        open={isChecklistDialogOpen}
        onClose={() => {
          if (canGoNext) setIsChecklistDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 26px 80px rgba(15, 23, 42, 0.36)",
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: "rgba(15, 23, 42, 0.68)",
            backdropFilter: "blur(3px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#172033",
            fontWeight: 900,
          }}
        >
          Conferência da capa
          <Chip
            size="small"
            label={`${checkedCount}/3`}
            sx={{
              bgcolor: canGoNext ? "#e8f5ef" : "#eef3f8",
              color: canGoNext ? "#2f7d62" : "#526173",
              fontWeight: 900,
            }}
          />
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          <Typography variant="body2" sx={{ color: "#667085", mb: 2 }}>
            Antes de enviar a capa pronta, confirme estes pontos.
          </Typography>

          <Stack spacing={1.25}>
            {checklistItems.map((item, index) => (
              <Box
                key={item.text}
                onClick={() => item.onChange(!item.checked)}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  p: 1.4,
                  borderRadius: 1,
                  border: item.checked ? "1px solid #a7d8c3" : "1px solid #e2e8f0",
                  bgcolor: item.checked ? "#f0faf5" : "#fbfdff",
                  cursor: "pointer",
                  transition: "all 180ms ease",
                  "&:hover": {
                    borderColor: item.checked ? "#79c6a4" : "#b7c4d4",
                    bgcolor: item.checked ? "#edf8f3" : "#f6f9fc",
                  },
                }}
              >
                <Checkbox
                  size="small"
                  checked={item.checked}
                  onChange={(e) => item.onChange(e.target.checked)}
                  onClick={(event) => event.stopPropagation()}
                  sx={{ p: 0.25, color: "#8a98aa", "&.Mui-checked": { color: "#2f7d62" } }}
                />
                <Box>
                  <Typography variant="caption" fontWeight={900} sx={{ color: "#31475f" }}>
                    Item {index + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.45, color: "#3f4a5a" }}>
                    {item.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, bgcolor: "#f8fafc" }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: 8,
              px: 2.5,
              color: "#d32f2f !important",
              bgcolor: "#fee7ec",
              textTransform: "none",
              fontWeight: 800,
              "&:hover": { bgcolor: "#fbd0da" },
            }}
          >
            Voltar ao miolo
          </Button>
          <Button
            variant="contained"
            disabled={!canGoNext}
            onClick={() => setIsChecklistDialogOpen(false)}
            sx={{
              borderRadius: 8,
              px: 3,
              bgcolor: canGoNext ? "#2f7d62" : "#9bc9b6",
              color: "#fff !important",
              textTransform: "none",
              fontWeight: 900,
              boxShadow: canGoNext ? "0 12px 24px rgba(47, 125, 98, 0.28)" : "none",
              opacity: canGoNext ? 1 : 0.62,
              "&:hover": { bgcolor: canGoNext ? "#25684f" : "#9bc9b6", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: "#9bc9b6",
                color: "#fff !important",
              },
            }}
          >
            Conferir medidas
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid #dfe7f2",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Typography fontWeight={900} mb={0.5} sx={{ color: "#1f2a37", fontSize: 18 }}>
              Configurações da capa
            </Typography>
            <Typography variant="body2" mb={2} sx={{ color: "#667085" }}>
              Esses dados calculam lombada, sangria e tamanho final.
            </Typography>

            <Typography variant="caption" fontWeight={800} mb={0.75} display="block">
              Tipo de papel do miolo
            </Typography>
            <Select
              fullWidth
              size="small"
              value={paperId}
              onChange={(e) => setPaperId(e.target.value)}
            >
              {PAPER_TYPES.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label} ({p.thicknessMicron} µm)
                </MenuItem>
              ))}
            </Select>

            <Box
              mt={2}
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 1,
                px: 1.25,
                py: 0.5,
                bgcolor: "#fbfdff",
              }}
            >
              <FormControlLabel
                control={
                  <Switch checked={hasFlap} onChange={(e) => setHasFlap(e.target.checked)} />
                }
                label="Livro COM orelha?"
              />
            </Box>

            <Grid container spacing={1.25} mt={1}>
              {measureItems.map((item) => (
                <Grid item xs={6} key={item.label}>
                  <Box
                    sx={{
                      border: "1px solid #e6edf5",
                      borderRadius: 1,
                      p: 1.25,
                      bgcolor: "#ffffff",
                      minHeight: 66,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#758195", fontWeight: 800 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={900} sx={{ color: "#1f2a37", mt: 0.5 }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>

        {/* COLUNA DIREITA - mock das medidas */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              border: "1px solid #dfe7f2",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
              height: "100%",
            }}
          >
            <Box width="100%" maxWidth={720}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                mb={2}
              >
                <Box>
                  <Typography fontWeight={900} sx={{ color: "#1f2a37", fontSize: 20 }}>
                    Prévia técnica
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#667085" }}>
                    Distribuição da contracapa, lombada e capa.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={`L ${totalWidthLabel}`} sx={{ fontWeight: 800 }} />
                  <Chip
                    size="small"
                    label={`A ${formatMm(totalHeightMm)}`}
                    sx={{ fontWeight: 800 }}
                  />
                </Stack>
              </Stack>

              {/* Mock da capa baseado no print */}
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "#f6f8fb",
                  border: "1px solid #e0e7f0",
                  borderRadius: 1,
                  p: { xs: 1.5, md: 2 },
                }}
              >
                {/* área geral */}
                <Box
                  sx={{
                    border: "2px dashed #9aa9bb",
                    p: 1,
                    position: "relative",
                    height: 260,
                    display: "flex",
                    flexDirection: "row",
                    bgcolor: "#ffffff",
                    borderRadius: 1,
                  }}
                >
                  {/* contra-capa */}
                  <Box
                    sx={{
                      flex: 1,
                      mr: 0.5,
                      bgcolor: "#eaf4ff",
                      border: "1px solid #9fc3e6",
                      position: "relative",
                      borderRadius: 0.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ position: "absolute", top: 7, left: 8, color: "#526173" }}
                    >
                      Contracapa
                    </Typography>
                  </Box>

                  {/* lombada */}
                  <Box
                    sx={{
                      width: 24,
                      mx: 0.5,
                      bgcolor: "#dff3ea",
                      border: "1px solid #79c6a4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ writingMode: "vertical-rl", color: "#2f7d62" }}
                    >
                      Lombada {formatMm(spineMm)}
                    </Typography>
                  </Box>

                  {/* capa */}
                  <Box
                    sx={{
                      flex: 1,
                      ml: 0.5,
                      bgcolor: "#eaf4ff",
                      border: "1px solid #9fc3e6",
                      position: "relative",
                      borderRadius: 0.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ position: "absolute", top: 7, right: 8, color: "#526173" }}
                    >
                      Capa
                    </Typography>
                  </Box>
                </Box>

                {/* legenda simples */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={2}>
                  <Chip
                    size="small"
                    label="Tracejado: sangria e segurança"
                    sx={{ bgcolor: "#eef3f8", color: "#526173", fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label="Azul: capa e contracapa"
                    sx={{ bgcolor: "#eaf4ff", color: "#315f8f", fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label="Verde: lombada automática"
                    sx={{ bgcolor: "#e8f5ef", color: "#2f7d62", fontWeight: 700 }}
                  />
                </Stack>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <FlowActions
        onBack={onClose}
        backLabel="Voltar ao miolo"
        onContinue={onNext}
        continueLabel="Continuar para a capa"
        disabled={!canGoNext}
        hint={
          canGoNext
            ? "Medidas e requisitos conferidos. Próximo: enviar o PDF da capa."
            : "Confira os requisitos e selecione o papel para continuar."
        }
      />
    </Box>
  );
}

/**
 * Tela B — upload + ajuste da capa (mock do editor laranja)
 * Versão A: apenas ajusta proporções (aspectRatio e largura da lombada)
 */
export function CoverUploadStep({
  hasFlap,
  setHasFlap,
  paper,
  spineMm,
  totalWidthMm,
  totalHeightMm,
  onBack,
  onClose,
  onFinish,
}) {
  const [scale, setScale] = useState(0);
  const [loadingCover, setLoadingCover] = useState(false);
  const [coverError, setCoverError] = useState("");
  const selection = useRef(0);
  useEffect(
    () => () => {
      selection.current += 1;
    },
    []
  );
  const [showAreas, setShowAreas] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [showMargin, setShowMargin] = useState(true);
  const [showInfo, setShowInfo] = useState(true);

  // ESTADO PARA ARMAZENAR A CAPA
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverImageDataUrl, setCoverImageDataUrl] = useState(null);

  const formatMm = (value) => (value ? `${value.toFixed(1).replace(".", ",")} mm` : "-");

  const ready = Boolean(coverFile && coverPreview && coverImageDataUrl && !loadingCover);
  const handleSelectCover = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const request = ++selection.current;
    setCoverFile(null);
    setCoverPreview(null);
    setCoverImageDataUrl(null);
    setCoverError("");
    if (
      (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) ||
      file.size > 150 * 1024 * 1024 ||
      file.size === 0
    ) {
      setCoverError("Selecione um PDF de capa de até 150 MB.");
      setLoadingCover(false);
      return;
    }
    setLoadingCover(true);
    let pdf;
    try {
      const buffer = await file.arrayBuffer();
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
      pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
      pdf = await pdfjs.getDocument({ data: buffer }).promise;
      if (pdf.numPages !== 1)
        throw new Error("A capa deve ser um PDF de página única com contracapa, lombada e frente.");
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
      const preview = canvas.toDataURL("image/jpeg", 0.86);
      const image = canvasToProductCoverJpeg(canvas);
      if (selection.current !== request) return;
      setCoverFile(file);
      setCoverPreview(preview);
      setCoverImageDataUrl(image);
    } catch (error) {
      if (selection.current === request)
        setCoverError(
          error.message?.includes("página única")
            ? error.message
            : "Não foi possível abrir o PDF da capa. Confira o arquivo e tente novamente."
        );
    } finally {
      if (selection.current === request) setLoadingCover(false);
      if (pdf) await pdf.destroy();
    }
  };

  const handleSend = () => {
    if (!ready) return;

    if (typeof onFinish === "function") {
      onFinish(coverFile, coverImageDataUrl, {
        paperId: paper?.id,
        paperLabel: paper?.label,
        grammage: paper?.grammage,
        thicknessMicron: paper?.thicknessMicron,
      }); // devolve para o componente pai
    }
  };

  // Proporção real da arte (largura x altura)
  const aspectRatio =
    totalWidthMm && totalHeightMm ? `${totalWidthMm} / ${totalHeightMm}` : "2 / 1";

  // Largura relativa da lombada em %
  const spinePercent = totalWidthMm && spineMm ? Math.max(0.5, (spineMm / totalWidthMm) * 100) : 2; // garante algo visível

  return (
    <Box p={{ xs: 2, md: 3 }} sx={{ backgroundColor: "#f4f7fb", minHeight: "70vh" }}>
      <Card
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          borderRadius: 2,
          border: "1px solid #dfe7f2",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Box>
            <Typography fontWeight={900} sx={{ color: "#1f2a37", fontSize: 22 }}>
              Ajuste de tamanho da capa
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085", mt: 0.5 }}>
              Envie o PDF da capa e confira o encaixe nas áreas técnicas.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`L ${formatMm(totalWidthMm)}`}
              sx={{ bgcolor: "#eef3f8", color: "#31475f", fontWeight: 900 }}
            />
            <Chip
              size="small"
              label={`A ${formatMm(totalHeightMm)}`}
              sx={{ bgcolor: "#eef3f8", color: "#31475f", fontWeight: 900 }}
            />
            <Chip
              size="small"
              label={hasFlap ? "Com orelha" : "Sem orelha"}
              sx={{ bgcolor: "#e8f5ef", color: "#2f7d62", fontWeight: 900 }}
            />
          </Stack>
        </Stack>
      </Card>

      {coverError && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {coverError}
        </Alert>
      )}
      {loadingCover && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" mb={1}>
            Preparando a prévia da capa...
          </Typography>
          <LinearProgress />
        </Box>
      )}
      <Grid container spacing={2.5} mb={2.5}>
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              p: 2.5,
              height: "100%",
              borderRadius: 2,
              border: "1px solid #dfe7f2",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Typography fontWeight={900} sx={{ color: "#1f2a37", fontSize: 18 }}>
              Arquivo da capa
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085", mt: 0.5, mb: 2 }}>
              Use um PDF em página única no tamanho informado.
            </Typography>

            <Button
              variant="contained"
              component="label"
              sx={{
                bgcolor: "#31475f",
                color: "#fff !important",
                fontWeight: 800,
                textTransform: "none",
                borderRadius: 8,
                px: 2.5,
                boxShadow: "0 10px 22px rgba(49, 71, 95, 0.22)",
                "&:hover": {
                  bgcolor: "#26384c",
                  color: "#fff !important",
                },
              }}
            >
              Selecionar capa
              <input type="file" hidden accept="application/pdf" onChange={handleSelectCover} />
            </Button>

            {coverFile && (
              <Box
                mt={2}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  border: "1px solid #dfe7f2",
                  bgcolor: "#fbfdff",
                }}
              >
                <Typography variant="caption" sx={{ color: "#758195", fontWeight: 800 }}>
                  Arquivo selecionado
                </Typography>
                <Typography variant="body2" fontWeight={900} sx={{ color: "#1f2a37", mt: 0.5 }}>
                  {coverFile.name}
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            component="fieldset"
            disabled={!ready}
            aria-disabled={!ready}
            sx={{
              p: 2.5,
              m: 0,
              minWidth: 0,
              opacity: ready ? 1 : 0.5,
              height: "100%",
              borderRadius: 2,
              border: "1px solid #dfe7f2",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Typography fontWeight={900} sx={{ color: "#1f2a37", fontSize: 18 }}>
              Ajustes
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085", mt: 0.5, mb: 2 }}>
              Ajuste apenas a visualização da arte, sem alterar as medidas.
            </Typography>

            <Box
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 1,
                px: 1.25,
                py: 0.5,
                bgcolor: "#fbfdff",
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    disabled={!ready}
                    checked={hasFlap}
                    onChange={(e) => setHasFlap(e.target.checked)}
                    color="default"
                  />
                }
                label="Tem orelha?"
              />
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" fontWeight={800} sx={{ color: "#526173" }}>
                Ajuste de tamanho
              </Typography>
              <Chip
                size="small"
                label={`${scale >= 0 ? "+" : ""}${scale}%`}
                sx={{ bgcolor: "#eef3f8", color: "#31475f", fontWeight: 900 }}
              />
            </Stack>
            <Slider
              size="small"
              disabled={!ready}
              value={scale}
              onChange={(_, v) => setScale(v)}
              min={-50}
              max={50}
              sx={{
                color: "#31475f",
                "& .MuiSlider-thumb": {
                  boxShadow: "0 6px 16px rgba(49, 71, 95, 0.22)",
                },
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            component="fieldset"
            disabled={!ready}
            aria-disabled={!ready}
            sx={{
              p: 2.5,
              m: 0,
              minWidth: 0,
              opacity: ready ? 1 : 0.5,
              height: "100%",
              borderRadius: 2,
              border: "1px solid #dfe7f2",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Typography fontWeight={900} sx={{ color: "#1f2a37", fontSize: 18 }}>
              Visualização
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085", mt: 0.5, mb: 1.5 }}>
              Ligue ou desligue camadas auxiliares da prévia.
            </Typography>

            <Grid container spacing={1}>
              {[
                { label: "Áreas", checked: showAreas, onChange: setShowAreas },
                { label: "Legenda", checked: showLegend, onChange: setShowLegend },
                { label: "Margem", checked: showMargin, onChange: setShowMargin },
                { label: "Info", checked: showInfo, onChange: setShowInfo },
              ].map((item) => (
                <Grid item xs={6} key={item.label}>
                  <Box
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 1,
                      px: 1,
                      py: 0.25,
                      bgcolor: item.checked ? "#f0faf5" : "#fbfdff",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={!ready}
                          checked={item.checked}
                          onChange={(e) => item.onChange(e.target.checked)}
                          color="default"
                        />
                      }
                      label={item.label}
                      sx={{
                        m: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#31475f",
                        },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            {!ready && (
              <Typography variant="caption" display="block" mt={2} color="text.secondary">
                Selecione e carregue a capa para habilitar os controles.
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      {ready ? (
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderRadius: 2,
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: "1px solid #dfe7f2",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box width="100%" maxWidth={1100} textAlign="center">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              mb={2}
              textAlign="left"
            >
              <Box>
                <Typography fontWeight={900} sx={{ color: "#1f2a37", fontSize: 20 }}>
                  Área de encaixe da capa
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  Tamanho final da arte: {formatMm(totalWidthMm)} (L) x {formatMm(totalHeightMm)}{" "}
                  (A)
                </Typography>
              </Box>
              <Chip
                size="small"
                label={coverPreview ? "PDF carregado" : "Aguardando PDF"}
                sx={{
                  bgcolor: coverPreview ? "#e8f5ef" : "#eef3f8",
                  color: coverPreview ? "#2f7d62" : "#526173",
                  fontWeight: 900,
                }}
              />
            </Stack>

            <Box
              sx={{
                position: "relative",
                mx: "auto",
                width: "100%",
                maxWidth: 1000,
                aspectRatio,
                bgcolor: "#fff",
                overflow: "hidden",
                border: "1px solid #d8dee6",
                borderRadius: 1,
                boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.03)",
              }}
            >
              {/* ----------------------------------------------------- */}
              {/* 1) A CAPA — COM TRANSPARÊNCIA e escala                */}
              {/* ----------------------------------------------------- */}
              {coverPreview && (
                <img
                  src={coverPreview}
                  alt="Preview capa"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: 0.82, // 🔥 sua transparência
                    transform: `scale(${1 + scale / 100})`,
                    transformOrigin: "center center",
                    zIndex: 1,
                  }}
                />
              )}

              {/* ----------------------------------------------------- */}
              {/* 2) ÁREA DA CAPA / CONTRACAPA (roxo claro)             */}
              {/* ----------------------------------------------------- */}
              {showAreas && (
                <>
                  {/* Contra-capa */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "6%",
                      bottom: "6%",
                      left: "0%",
                      width: "50%",
                      bgcolor: "rgba(155,130,255,0.35)",
                      border: "2px solid rgba(100,80,200,0.7)",
                      zIndex: 2,
                    }}
                  />

                  {/* Capa */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "6%",
                      bottom: "6%",
                      right: "0%",
                      width: "50%",
                      bgcolor: "rgba(155,130,255,0.35)",
                      border: "2px solid rgba(100,80,200,0.7)",
                      zIndex: 2,
                    }}
                  />
                </>
              )}

              {/* ----------------------------------------------------- */}
              {/* 3) LOMBADA (verde translúcido)                        */}
              {/* ----------------------------------------------------- */}
              <Box
                sx={{
                  position: "absolute",
                  top: "6%",
                  bottom: "6%",
                  left: `${50 - spinePercent / 2}%`,
                  width: `${spinePercent}%`,
                  bgcolor: "rgba(76,175,80,.35)",
                  border: "2px dashed #4caf50",
                  zIndex: 3,
                }}
              />

              {/* ----------------------------------------------------- */}
              {/* 4) LINHA DE CORTE (vermelho tracejado)                */}
              {/* ----------------------------------------------------- */}
              {showAreas && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    border: "4px solid rgba(229,57,53,0.9)",
                    borderRadius: 1,
                    zIndex: 4,
                  }}
                />
              )}

              {/* ----------------------------------------------------- */}
              {/* 5) MARGEM DE SEGURANÇA (rosa)                         */}
              {/* ----------------------------------------------------- */}
              {showMargin && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: "4%",
                    border: "3px dashed #e53935", // 🔴 vermelho sólido
                    background: "rgba(255,205,210,.25)", // suave, rosado claro
                    zIndex: 3,
                  }}
                />
              )}

              {/* ----------------------------------------------------- */}
              {/* 6) LEGENDA COMPLETA (igual UICLAP)                    */}
              {/* ----------------------------------------------------- */}
              {/* LEGENDA COMPLETA (sem emojis, com linhas reais) */}
              {showLegend && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 12,
                    bottom: 12,
                    bgcolor: "rgba(255,255,255,0.95)",
                    p: 1.5,
                    borderRadius: 1,
                    fontSize: "12px",
                    zIndex: 10,
                    boxShadow: "0 2px 6px rgba(0,0,0,.15)",
                    minWidth: 150,
                  }}
                >
                  {/* Linha de corte */}
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Box
                      sx={{
                        width: 26,
                        height: 0,
                        borderBottom: "2px dashed #e53935",
                        mr: 1,
                      }}
                    ></Box>
                    <span>Linha de corte</span>
                  </Box>

                  {/* Margem de segurança */}
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Box
                      sx={{
                        width: 26,
                        height: 0,
                        borderBottom: "2px solid #e91e63",
                        mr: 1,
                      }}
                    ></Box>
                    <span>Margem de segurança</span>
                  </Box>

                  {/* Área da capa / contracapa */}
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Box
                      sx={{
                        width: 26,
                        height: 12,
                        bgcolor: "rgba(155,130,255,0.35)",
                        border: "1px solid rgba(100,80,200,0.7)",
                        mr: 1,
                      }}
                    ></Box>
                    <span>Capa / Contracapa</span>
                  </Box>

                  {/* Lombada */}
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 26,
                        height: 12,
                        bgcolor: "rgba(76,175,80,.35)",
                        border: "1px solid #4caf50",
                        mr: 1,
                      }}
                    ></Box>
                    <span>Lombada</span>
                  </Box>
                </Box>
              )}

              {/* ----------------------------------------------------- */}
              {/* 7) INFO LATERAL                                       */}
              {/* ----------------------------------------------------- */}
              {showInfo && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    bottom: 12,
                    bgcolor: "rgba(255,255,255,0.9)",
                    p: 1,
                    borderRadius: 1,
                    zIndex: 10,
                    fontSize: "12px",
                  }}
                >
                  <Typography variant="caption" display="block">
                    Escala: {scale >= 0 ? "+" : ""}
                    {scale}% (somente na arte)
                  </Typography>
                  <Typography variant="caption" display="block">
                    {hasFlap ? "Livro COM orelha" : "Livro SEM orelha"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            p: 5,
            bgcolor: "#fff",
            border: "1px dashed #b9cfc1",
            borderRadius: 3,
          }}
        >
          <Typography variant="h6">Sua capa aparecerá aqui</Typography>
          <Typography variant="body2" mt={1} color="text.secondary">
            Selecione o PDF da capa para conferir o encaixe e liberar os ajustes.
          </Typography>
        </Box>
      )}
      <FlowActions
        onBack={onBack}
        backLabel="Voltar às medidas"
        onContinue={handleSend}
        continueLabel="Continuar para informações"
        disabled={!ready}
        busy={loadingCover}
        hint={
          ready
            ? "Confira o encaixe da arte antes de continuar."
            : "Envie o PDF da capa para liberar ajustes, visualização e a próxima etapa."
        }
      />
    </Box>
  );
}
